/**
 * Centralized environment variable utilities
 * Single source of truth for all environment configuration
 */

import { isGovEarnerHost } from './workspaceAutoSelect';

/** Public Trace API gateway for GovEarner / SPARC RFP production (not raw ACA ingress). */
const TRACE_API_PUBLIC_URL = 'https://trace-api.alpyne.ai';

/**
 * GovEarner browser calls must use the Trace API gateway, never *.azurecontainerapps.io.
 * Legacy deploys set RUNTIME_CONFIG.API_URL from ACA FQDN — normalize here.
 */
function normalizeGovEarnerApiUrl(url: string): string {
  if (/\.azurecontainerapps\.io/i.test(url)) {
    return TRACE_API_PUBLIC_URL;
  }
  return url;
}

/**
 * Get the API base URL from environment variables
 * Priority:
 * 1. window.RUNTIME_CONFIG.API_URL (runtime config from entrypoint script — explicitly set by admin)
 * 2. When on dashboard host but RUNTIME_CONFIG not set: same-host API (HTTP :8000 or HTTPS :443 via nginx)
 * 3. VITE_API_URL (environment variable - build time)
 * 4. On-prem auto-detection
 * 5. Default production URL
 */
export function getApiBaseUrl(): string {
  // Check for runtime config first (set by entrypoint script — takes priority over heuristics).
  // On-prem deployments set INJECT_API_URL → runtime-config.js → RUNTIME_CONFIG.API_URL.
  // This value is explicitly configured by the admin and should always win.
  if (typeof window !== 'undefined' && (window as any).RUNTIME_CONFIG?.API_URL) {
    let runtimeUrl = (window as any).RUNTIME_CONFIG.API_URL as string;
    // On HTTPS dashboard hosts, strip any lingering :8000 — that port is HTTP-only (TLS handshake fails).
    if (typeof window !== 'undefined' && window.location.protocol === 'https:' && runtimeUrl.includes(':8000')) {
      runtimeUrl = runtimeUrl.replace(':8000', '');
    }
    if (typeof window !== 'undefined' && isGovEarnerHost()) {
      const publicUrl = normalizeGovEarnerApiUrl(runtimeUrl);
      if (import.meta.env.DEV) {
        console.log('[ENV DEBUG] GovEarner host — using Trace API URL:', publicUrl);
      }
      return publicUrl;
    }
    if (import.meta.env.DEV) {
      console.log('[ENV DEBUG] Using runtime config API_URL:', runtimeUrl);
    }
    return runtimeUrl;
  }

  // GovEarner production: always use Trace API gateway when runtime config is absent.
  if (typeof window !== 'undefined' && isGovEarnerHost()) {
    if (import.meta.env.DEV) {
      console.log('[ENV DEBUG] GovEarner host (no RUNTIME_CONFIG), using Trace API URL:', TRACE_API_PUBLIC_URL);
    }
    return TRACE_API_PUBLIC_URL;
  }

  // Fallback: when served from a known dashboard host but no RUNTIME_CONFIG, use same-host nginx proxy.
  // HTTP: port 8000 (nginx server block at :8000 proxies all → API).
  // HTTPS: no port (port 443) — nginx HTTPS server block proxies all API paths → API.
  //        Never append :8000 on HTTPS; port 8000 is HTTP-only and the browser blocks the request.
  if (typeof window !== 'undefined' && _isDashboardHost(window.location.hostname)) {
    const protocol = window.location.protocol;
    const hostname = window.location.hostname;
    const apiUrl = protocol === 'https:'
      ? `https://${hostname}`
      : `http://${hostname}:8000`;
    if (import.meta.env.DEV) {
      console.log('[ENV DEBUG] Dashboard host detected (no RUNTIME_CONFIG), using same-host API URL:', apiUrl);
    }
    return apiUrl;
  }

  const envUrl = import.meta.env.VITE_API_URL;

  // If VITE_API_URL is set, use it
  if (envUrl) {
    return envUrl;
  }

  // Check for on-prem deployment type
  const deploymentType = import.meta.env.VITE_DEPLOYMENT_TYPE ||
    (typeof window !== 'undefined' && (window as any).RUNTIME_CONFIG?.DEPLOYMENT_TYPE);

  // On-prem auto-detection: check if deployment type is onprem or hostname suggests on-prem
  const isOnPrem = deploymentType === 'onprem' ||
    (typeof window !== 'undefined' && _isOnPremHostname(window.location.hostname));

  if (isOnPrem) {
    if (typeof window !== 'undefined') {
      const hostname = window.location.hostname;
      const protocol = window.location.protocol;
      const port = window.location.port;
      // In local dev (localhost/127.0.0.1), API is on a fixed port (8000/8001), not the frontend port.
      // Otherwise we would send /projects, /api/notifications etc. to the Vite dev server and get failures.
      const isLocalDev = import.meta.env.DEV && (hostname === 'localhost' || hostname === '127.0.0.1');
      // Vite dev on RFC1918 host (e.g. http://10.0.215.36:5176): must not use the SPA port for API.
      const isDevOnPrivateLan =
        import.meta.env.DEV &&
        (/^10\./.test(hostname) ||
          /^192\.168\./.test(hostname) ||
          /^172\.(1[6-9]|2[0-9]|3[0-1])\./.test(hostname));
      // HTTPS pages must not target :8000 — that port is HTTP-only on the dashboard VM (TLS handshake fails).
      if (protocol === 'https:') {
        const apiUrl = `https://${hostname}`;
        if (import.meta.env.DEV) {
          console.log('[ENV DEBUG] On-prem HTTPS: same-origin API base (no :8000):', apiUrl);
        }
        return apiUrl;
      }
      const apiPort = isLocalDev
        ? (import.meta.env.VITE_API_PORT || '8001')
        : isDevOnPrivateLan
          ? (import.meta.env.VITE_API_PORT || '8000')
          : (port || '8000');
      const apiUrl = `http://${hostname}:${apiPort}`;
      if (import.meta.env.DEV) {
        console.log('[ENV DEBUG] On-prem detected, using API URL:', apiUrl, isLocalDev ? '(local dev default)' : '');
      }
      return apiUrl;
    }
    return 'http://localhost:8000';
  }
  
  // In development, default to localhost:8000 if no env var is set
  // This handles the case where frontend runs on localhost:3000 or any other port
  if (import.meta.env.DEV && !envUrl) {
    const devApiUrl = 'http://localhost:8000';
    if (typeof window !== 'undefined' && import.meta.env.DEV) {
      console.log('[ENV DEBUG] Development mode - using default API URL:', devApiUrl);
      console.log('[ENV DEBUG] Frontend running on:', window.location.origin);
      console.log('[ENV DEBUG] To override, set VITE_API_URL in .env.local');
      console.log('[ENV DEBUG] Deployment type:', deploymentType || 'auto-detected');
    }
    return devApiUrl;
  }
  
  // Default production URL
  return 'https://trace-api.alpyne.ai';
}

/** Keystone on-prem dashboard hostname (pretty name). API: HTTP same-host :8000, HTTPS same-host :443 (nginx → API). */
const KEYSTONE_DASHBOARD_HOSTNAMES = ['keyaidashboard', 'keyaidashboard.keystoneenvironmental.ca', 'keyaiapi'];
/** Dashboard VM IPs (prod .31, dev .36). Must match _isDashboardHost or HTTPS loads fall through and wrongly append :8000. */
const KEYSTONE_DASHBOARD_VM_IPS = ['10.0.245.31', '10.0.245.36'];

/** True when hostname is the dashboard (FQDN or known dashboard VM IPs). */
function _isDashboardHost(hostname: string): boolean {
  if (!hostname) return false;
  if (KEYSTONE_DASHBOARD_VM_IPS.includes(hostname)) return true;
  return KEYSTONE_DASHBOARD_HOSTNAMES.some((h) => hostname.toLowerCase() === h);
}

/**
 * Check if hostname suggests on-prem deployment
 * On-prem hostnames typically:
 * - Are localhost or local IPs (127.0.0.1, 192.168.x.x, 10.x.x.x, 172.16-31.x.x)
 * - Are internal domain names (e.g., *.local, *.internal, *.lan)
 * - Are Keystone pretty names (keyaidashboard, keyaiapi)
 * - Don't contain common cloud domains (alpyne.ai, azure, aws, etc.)
 */
function _isOnPremHostname(hostname: string): boolean {
  if (!hostname) return false;
  const lower = hostname.toLowerCase();
  
  // Keystone on-prem pretty names (so https://keyaidashboard works: same-origin API via nginx proxy)
  if (KEYSTONE_DASHBOARD_HOSTNAMES.some((h) => lower === h)) {
    return true;
  }
  
  // Localhost - always treat as on-prem in development
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return true;
  }
  
  // Private IP ranges
  if (/^192\.168\./.test(hostname) || 
      /^10\./.test(hostname) || 
      /^172\.(1[6-9]|2[0-9]|3[0-1])\./.test(hostname)) {
    return true;
  }
  
  // Internal domain patterns
  if (/\.local$/.test(hostname) || 
      /\.internal$/.test(hostname) || 
      /\.lan$/.test(hostname) ||
      /\.corp$/.test(hostname)) {
    return true;
  }
  
  // Not a cloud domain (alpyne.ai, azure, aws, etc.)
  if (!/\.(alpyne\.ai|azure|aws|gcp|cloud)/.test(hostname)) {
    // Could be on-prem, but be conservative - only return true if explicitly marked
    return false;
  }
  
  return false;
}

/** @deprecated Use _isDashboardHost for same-host:8000 logic. Kept for _isOnPremHostname callers. */
function _isDashboardHostname(hostname: string): boolean {
  return _isDashboardHost(hostname);
}

/**
 * Get the API key from environment variables (optional)
 */
export function getApiKey(): string | undefined {
  return import.meta.env.VITE_API_KEY;
}

/**
 * Get the deployment environment
 */
export function getEnvironment(): 'development' | 'staging' | 'production' {
  return (import.meta.env.MODE as 'development' | 'staging' | 'production') || 'development';
}

/**
 * Get the base URL for routing (Vite's BASE_URL)
 * Priority:
 * 1. window.RUNTIME_CONFIG.BASE_URL (runtime config from entrypoint script)
 * 2. VITE_BASE_URL (environment variable - build time)
 * 3. import.meta.env.BASE_URL (Vite's default BASE_URL)
 * 4. Default to '/'
 */
export function getBaseUrl(): string {
  // Check for runtime config first (set by entrypoint script)
  if (typeof window !== 'undefined' && (window as any).RUNTIME_CONFIG?.BASE_URL) {
    const runtimeBaseUrl = (window as any).RUNTIME_CONFIG.BASE_URL;
    if (typeof window !== 'undefined' && import.meta.env.DEV) {
      console.log('[ENV DEBUG] Using runtime config BASE_URL:', runtimeBaseUrl);
    }
    return runtimeBaseUrl;
  }
  
  // Check for VITE_BASE_URL environment variable
  const envBaseUrl = import.meta.env.VITE_BASE_URL;
  if (envBaseUrl) {
    return envBaseUrl;
  }
  
  // Fall back to Vite's BASE_URL or default
  return import.meta.env.BASE_URL || '/';
}

/**
 * Get the SAM.gov API key from environment variables
 * 
 * IMPORTANT: Vite only exposes environment variables that start with VITE_ to the browser.
 * Use VITE_SAM_API_KEY in your .env file, not SAM_API_KEY.
 */
export function getSAMApiKey(): string | undefined {
  // Vite only exposes VITE_ prefixed vars to client
  const key = import.meta.env.VITE_SAM_API_KEY;
  if (import.meta.env.DEV) {
    if (!key) {
      console.warn('[getSAMApiKey] No SAM API key found. Set VITE_SAM_API_KEY in your .env file');
      console.warn('[getSAMApiKey] Note: Vite only exposes env vars starting with VITE_ to the browser');
    } else {
      console.log('[getSAMApiKey] SAM API key found, length:', key.length);
    }
  }
  return key;
}

export interface SamGovIntegrationStateOptions {
  persistedEnabled?: boolean;
  userEnabled?: boolean;
  defaultEnabled?: boolean;
  userApiKey?: string;
  defaultApiKey?: string;
}

/**
 * Resolve SAM.gov enabled state and optional client API key.
 * Enabled by default (policy pack); backend proxy can use server-side SAM_API_KEY
 * when no VITE_SAM_API_KEY is available in the browser bundle.
 */
export function resolveSamGovIntegrationState(
  options: SamGovIntegrationStateOptions = {},
): { enabled: boolean; api_key?: string } {
  const runtimeApiKey = getSAMApiKey();
  const enabled =
    options.persistedEnabled ??
    options.userEnabled ??
    options.defaultEnabled ??
    true;

  return {
    enabled: Boolean(enabled),
    api_key: runtimeApiKey ?? options.userApiKey ?? options.defaultApiKey,
  };
}

